import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import UserMetadataForm from "./UserMetadataForm";
import ElectrospinningTable from "./ElectrospinningTable";
import { sendDataToBackend, getDataById, updateData, getSubmissionById } from "../../services/dataService";
import ResearchMetadataForm from "./ResearchMetadataForm";
import '../../styles/submission-workflow.css';
import '../../styles/submission/contribute.css';
import { useNotification } from '../../components/framework/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile } from '../../services/authService';
import { useConfirmation } from '../../contexts/ConfirmationContext';

import SuccessModal from "./modals/SuccessModal";

// New (not yet submitted) contributions are autosaved in this browser so a
// refresh or closed tab doesn't lose the work. Edits of existing submissions
// are not drafted - they are stored on the server already.
const DRAFT_KEY = 'esd-contribution-draft-v1';

// Study-level provenance fields: entered once in Study Information, stored on every run.
const PROVENANCE_KEYS = ['dataOrigin', 'extractionMethod', 'sourceLocation', 'diameterMethod', 'diameterNMeasured'];

const provenanceOf = (exp) => exp ? {
    dataOrigin: exp.dataOrigin || '',
    extractionMethod: exp.extractionMethod || '',
    sourceLocation: exp.sourceLocation || '',
    diameterMethod: exp.fiberProperty?.diameterMethod || '',
    diameterNMeasured: exp.fiberProperty?.diameterNMeasured ?? '',
} : {};

/**
 * Copies the study-level provenance onto each run. A field the user changed in
 * the form overrides every run; an untouched one only fills runs that lack it
 * (so per-run values from a bulk upload survive an edit).
 */
const applyProvenance = (runs, form, initial) => runs.map(run => {
    const pick = (key, current) => {
        const value = form[key];
        if (value === undefined || value === '') return current;
        const changed = String(value) !== String(initial[key] ?? '');
        return changed || current === undefined || current === null || current === '' ? value : current;
    };
    const n = pick('diameterNMeasured', run.fiberProperty?.diameterNMeasured);
    return {
        ...run,
        dataOrigin: pick('dataOrigin', run.dataOrigin),
        extractionMethod: pick('extractionMethod', run.extractionMethod),
        sourceLocation: pick('sourceLocation', run.sourceLocation),
        fiberProperty: {
            ...run.fiberProperty,
            diameterMethod: pick('diameterMethod', run.fiberProperty?.diameterMethod),
            diameterNMeasured: n === '' || n === undefined || n === null ? null : Number(n),
        },
    };
});

const STEP_LABELS = { contributor: 'Contributor', publication: 'Publication', data: 'Experimental data' };
// Drafts saved before the steps had names stored their number
const LEGACY_STEPS = { 1: 'contributor', 2: 'publication', 3: 'data' };

const readDraft = () => {
    try {
        const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
        return draft && draft.savedAt ? draft : null;
    } catch (_) {
        return null;
    }
};

const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch (_) { /* storage unavailable */ }
};

/** True when the grid holds anything worth restoring. */
const groupsHaveContent = (groups) => (groups || []).some(g =>
    g.setup?.polymerProperty?.polymerComponents?.length || g.setup?.solventProperty?.solventComponents?.length ||
    g.setup?.solutionProperty?.concentration || g.setup?.collectorProperty?.collectorType || g.setup?.needleProperty?.needleType ||
    g.rows?.some(r => r.fiberImages?.length || ['processParameter', 'ambientParameter', 'fiberProperty']
        .some(k => Object.entries(r[k] || {}).some(([f, v]) => !f.endsWith('Unit') && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)))));

export default function DataSubmissionWrapper() {
    const { id } = useParams(); // /edit-submission/:id edits that contribution
    const navigate = useNavigate();
    const isEditMode = !!id;

    const { isAuthenticated } = useAuth();
    // Signed-in contributors are known already: they start with the publication.
    const askContributor = !isEditMode && !isAuthenticated;
    const steps = askContributor ? ['contributor', 'publication', 'data'] : ['publication', 'data'];
    // An edit opens on the runs: that is what review feedback is about
    const [step, setStep] = useState(isEditMode ? 'data' : askContributor ? 'contributor' : 'publication');
    const [consent, setConsent] = useState(false); // given when submitting
    const { success, error, info } = useNotification();
    const confirm = useConfirmation();
    const [userMetadata, setUserMetadata] = useState(null);
    const [researchMetadata, setResearchMetadata] = useState(null);
    const [experimentData, setExperimentData] = useState([]); // Array for ElectrospinningTable
    const [loading, setLoading] = useState(isEditMode);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [imageOnly, setImageOnly] = useState(false);

    const [status, setStatus] = useState(null);
    const [newPolymers, setNewPolymers] = useState([]); // polymers defined in this contribution, awaiting approval
    const [submittedRuns, setSubmittedRuns] = useState(null);
    const isApproved = status === 'APPROVED';

    const [provenanceInitial, setProvenanceInitial] = useState({}); // as loaded, to tell edits apart
    const [pendingDraft, setPendingDraft] = useState(() => (isEditMode ? null : readDraft()));
    const [draftGroups, setDraftGroups] = useState(null); // handed to the table once, on restore
    const [currentGroups, setCurrentGroups] = useState(null);
    const [draftSavedAt, setDraftSavedAt] = useState(null);

    // Autosave (debounced) - only for new contributions, and only once the user
    // has decided what to do with an earlier draft.
    useEffect(() => {
        if (isEditMode || pendingDraft || showSuccessModal) return;
        const hasContent = userMetadata || researchMetadata || groupsHaveContent(currentGroups);
        if (!hasContent) return;
        const timer = setTimeout(() => {
            try {
                const savedAt = new Date().toISOString();
                localStorage.setItem(DRAFT_KEY, JSON.stringify({
                    savedAt, step, userMetadata, researchMetadata, imageOnly, groups: currentGroups,
                }));
                setDraftSavedAt(savedAt);
            } catch (_) { /* storage full or unavailable: autosave is best effort */ }
        }, 800);
        return () => clearTimeout(timer);
    }, [isEditMode, pendingDraft, showSuccessModal, step, userMetadata, researchMetadata, imageOnly, currentGroups]);

    const restoreDraft = () => {
        const draft = pendingDraft;
        setUserMetadata(draft.userMetadata || null);
        setResearchMetadata(draft.researchMetadata || null);
        setImageOnly(!!draft.imageOnly);
        if (groupsHaveContent(draft.groups)) {
            setDraftGroups(draft.groups);
            setCurrentGroups(draft.groups); // keep them in the autosave until the grid is shown again
        }
        const restored = LEGACY_STEPS[draft.step] || draft.step || 'publication';
        setStep(restored === 'contributor' && !askContributor ? 'publication' : restored);
        setPendingDraft(null);
        info("Your draft was restored.");
    };

    const discardDraft = () => {
        clearDraft();
        setPendingDraft(null);
    };

    useEffect(() => {
        const loadData = async () => {
            if (isEditMode) {
                try {
                    setLoading(true);
                    // Use getSubmissionById to fetch all related experiments for the batch
                    const data = await getSubmissionById(id);
                    if (data) {
                        setUserMetadata(data.userMetadata);
                        const first = (data.experimentData || [])[0];
                        setProvenanceInitial(provenanceOf(first));
                        setResearchMetadata({ ...data.researchMetadata, ...provenanceOf(first) });
                        if (data.status) setStatus(data.status);
                        // ElectrospinningTable expects an array of ExperimentDataDTO
                        // The API returns DTO which has experimentData list, but updateExperiment handles list retrieval.
                        // My `getSubmitDataDTO` returns object with `experimentData` property which is a LIST.
                        // So we pass that list.
                        setExperimentData(data.experimentData || []);
                        setImageOnly(!!data.imageOnly);
                    }
                } catch (err) {
                    console.error("Failed to load submission:", err);
                    error("Failed to load data for editing.");
                    navigate("/my-submissions");
                } finally {
                    setLoading(false);
                }
            }
        };
        loadData();
    }, [id, isEditMode, navigate]);

    // A signed-in contributor's details come from their profile.
    useEffect(() => {
        if (askContributor && step === 'contributor') return;
        if (!askContributor && step === 'contributor') setStep('publication');
    }, [askContributor, step]);

    useEffect(() => {
        if (isEditMode || !isAuthenticated || userMetadata) return;
        getUserProfile()
            .then(profile => setUserMetadata({ name: profile.name || '', email: profile.email || '', orcid: profile.orcid || '' }))
            .catch(() => { /* the backend uses the account's details anyway */ });
    }, [isEditMode, isAuthenticated, userMetadata]);

    const goTo = (next) => {
        setStep(next);
        window.scrollTo(0, 0);
    };

    const handleUserMetadataNext = (data) => {
        setUserMetadata(data);
        goTo('publication');
    };

    const handleResearchMetadataNext = async (data) => {
        setResearchMetadata(data);
        goTo('data');
    };

    // Excel rows carry their paper's metadata. Fill Study Information from the file
    // when it was left blank, and warn when the file mixes papers (one submission = one DOI).
    const handleImportMetadata = (rowsMetadata) => {
        const dois = [...new Set(rowsMetadata.map(m => m.doi?.trim()).filter(Boolean))];
        if (dois.length > 1) {
            info(`The file contains ${dois.length} different DOIs, but one contribution covers one publication. ` +
                "All imported rows will be submitted under the DOI in Study Information; please upload one file per paper.");
        }
        const fromFile = rowsMetadata.find(m => m.doi || m.publicationTitle);
        if (!fromFile) return;
        const current = researchMetadata || {};
        if (current.doi?.trim() || current.publicationTitle?.trim()) {
            if (dois.length === 1 && current.doi?.trim() && dois[0] !== current.doi.trim()) {
                info(`The file's DOI (${dois[0]}) differs from the one in Study Information (${current.doi}). ` +
                    "Study Information is kept.");
            }
            return;
        }
        setResearchMetadata({
            ...current,
            doi: fromFile.doi || "",
            publicationTitle: fromFile.publicationTitle || "",
            abstract: fromFile.abstract || current.abstract || "",
            deviceManufacturer: fromFile.deviceManufacturer || current.deviceManufacturer || "",
            deviceModel: fromFile.deviceModel || current.deviceModel || "",
            customDevice: fromFile.customDevice === "true" || fromFile.customDevice === true,
        });
        info("Study Information (DOI, title) was filled in from the Excel file.");
    };

    // apiRequest errors read "Request failed: <status> - <body>"; show the backend's message when present.
    const backendMessage = (e, fallback) => {
        const match = /Request failed: \d+ - (.*)$/s.exec(e?.message || "");
        if (match) {
            try {
                const body = JSON.parse(match[1]);
                if (body?.message) return body.message;
            } catch (_) {
                if (match[1] && match[1].length < 300) return match[1];
            }
        }
        return fallback;
    };

    const handleExperimentDataSubmit = async (dataFromTable, overrideResearchMetadata = null) => {
        // dataFromTable is the list of experiment data rows (flattened or grouped, but backend expects list)

        const study = overrideResearchMetadata || researchMetadata || {};
        // Provenance lives on each run; the publication metadata keeps only its own fields.
        const publication = Object.fromEntries(Object.entries(study).filter(([k]) => !PROVENANCE_KEYS.includes(k)));
        const payload = {
            userMetadata: { ...(userMetadata || {}), consentTerms: isEditMode ? (userMetadata?.consentTerms ?? true) : consent },
            researchMetadata: publication,
            experimentData: applyProvenance(dataFromTable, study, provenanceInitial),
            imageOnly: imageOnly,
        };

        console.log("Sending payload:", payload);

        // Runs removed in the form are deleted on save: say so first
        if (isEditMode) {
            const kept = new Set(payload.experimentData.map(r => r.recordId).filter(Boolean));
            const removed = experimentData.filter(r => r.recordId && !kept.has(r.recordId) && r.status !== 'APPROVED');
            if (removed.length > 0) {
                const ok = await confirm({
                    title: `Delete ${removed.length} run${removed.length === 1 ? '' : 's'}?`,
                    message: `You removed ${removed.length} run${removed.length === 1 ? '' : 's'} from this contribution. `
                        + `Saving deletes ${removed.length === 1 ? 'it' : 'them'}, with ${removed.length === 1 ? 'its' : 'their'} images. This cannot be undone.`,
                    confirmLabel: "Save and delete",
                    type: "danger",
                });
                if (!ok) return;
            }
        }

        try {
            let response;
            if (isEditMode) {
                response = await updateData(id, payload);
                success("Your changes are saved.");
                navigate("/my-submissions", { state: { saved: { submissionId: Number(id), result: response } } });
            } else {
                response = await sendDataToBackend(payload);
                clearDraft();
                setNewPolymers([...new Set(payload.experimentData
                    .flatMap(run => run.polymerProperty?.polymerComponents || [])
                    .filter(c => c?.newPolymer && c.polymerName)
                    .map(c => c.polymerName))]);
                setSubmittedRuns(payload.experimentData.length);
                setShowSuccessModal(true);
                // success(`Success: ${response}`); // Notification redundant with modal
            }
        } catch (e) {
            console.error("Error submitting data:", e);
            error(backendMessage(e, isEditMode ? "Failed to update contribution." : "Failed to send data."));
        }
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        // Reset form or redirect? Probably reset or reload.
        // For now, let's reload to clear state cleanly or navigate to self
        window.location.reload();
    };

    const handleNavigateToMySubmissions = () => {
        setShowSuccessModal(false);
        navigate("/my-submissions");
    };

    if (loading) {
        return (
            <div className="cx"><div className="cx-container text-center py-5 text-muted">
                <div className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>Loading the contribution…
            </div></div>
        );
    }

    // Determine if any part of the data is locked
    const hasLockedData = isApproved || experimentData.some(r => r.status === 'APPROVED');

    return (
        <div className="cx">
            <div className={`cx-container ${step === 'data' ? 'cx-container--wide' : ''}`}>

            <SuccessModal
                show={showSuccessModal}
                newPolymers={newPolymers}
                runs={submittedRuns}
                onClose={handleCloseSuccessModal}
                onNavigateToMySubmissions={handleNavigateToMySubmissions}
            />

            <header className="cx-pagehead">
                <div>
                    {isEditMode && (
                        <Link to="/my-submissions" className="cx-backlink"><i className="bi bi-arrow-left me-1" aria-hidden="true"></i>My contributions</Link>
                    )}
                    <h1>{isEditMode ? `Edit contribution #${id}` : 'Contribute experimental data'}</h1>
                    <p>
                        {isEditMode
                            ? (researchMetadata?.publicationTitle || researchMetadata?.doi || 'Edited runs go back to review when you save. Approved runs are published and stay locked.')
                            : 'Add electrospinning runs from a publication or your own lab. A moderator reviews them before they are published.'}
                    </p>
                </div>
                <nav aria-label="Contribution steps">
                    <ol className="cx-stepper">
                        {steps.map((key, i) => {
                            const current = steps.indexOf(step);
                            const state = i === current ? 'is-current' : i < current ? 'is-done' : '';
                            return (
                                <li key={key} className={state}>
                                    <button type="button" disabled={i >= current} onClick={() => goTo(key)}
                                        aria-current={i === current ? 'step' : undefined}>
                                        <span className="cx-step-num">{i < current ? <i className="bi bi-check-lg" aria-hidden="true"></i> : i + 1}</span>
                                        {STEP_LABELS[key]}
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </nav>
            </header>

            <div className="cx-meta">
                {userMetadata?.name && !askContributor && (
                    <span className="cx-identity">
                        <span className="cx-avatar" aria-hidden="true">{userMetadata.name.trim().charAt(0).toUpperCase()}</span>
                        <span>Contributing as <strong>{userMetadata.name}</strong>{userMetadata.email ? ` · ${userMetadata.email}` : ''}</span>
                    </span>
                )}
                {!isEditMode && !pendingDraft && draftSavedAt && (
                    <span aria-live="polite"><i className="bi bi-cloud-check me-1"></i>Draft saved {new Date(draftSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                )}
                {!isEditMode && (
                    <span className="ms-auto cx-hide-narrow">Many runs in a spreadsheet? <Link to="/bulk-upload">Use the bulk upload</Link></span>
                )}
            </div>

            {pendingDraft && (
                <div className="cx-notice cx-notice--info" role="status">
                    <span>
                        <i className="bi bi-clock-history me-2"></i>
                        You have an unsent contribution from {new Date(pendingDraft.savedAt).toLocaleString()}.
                    </span>
                    <span className="d-flex gap-2">
                        <button type="button" className="cx-btn cx-btn--primary cx-btn--sm" onClick={restoreDraft}>Continue where I left off</button>
                        <button type="button" className="cx-btn cx-btn--secondary cx-btn--sm" onClick={discardDraft}>Start over</button>
                    </span>
                </div>
            )}

            {isEditMode && (
                <div className="cx-notice cx-notice--info">
                    <span>
                        <i className="bi bi-info-circle me-2"></i>
                        Edited runs go back to review when you save.
                        {hasLockedData && <> <strong>Approved</strong> runs are published and stay locked.</>}
                        {experimentData.some(r => r.status === 'REJECTED') && <> <strong>Rejected</strong> runs stay as they are unless you choose <em>Revise and resubmit</em>.</>}
                    </span>
                </div>
            )}

            {/* Workflow Content */}
            <div>
                {step === 'contributor' && (
                    <UserMetadataForm
                        onNext={handleUserMetadataNext}
                        initialValues={userMetadata}
                        // Lock user metadata if in edit mode (any status)
                        isRestricted={isEditMode}
                    />
                )}
                {step === 'publication' && (
                    <ResearchMetadataForm
                        onNext={handleResearchMetadataNext}
                        onBack={askContributor ? () => goTo('contributor') : null}
                        initialValues={researchMetadata}
                        isRestricted={isApproved}
                    />
                )}
                {step === 'data' && (
                    <ElectrospinningTable
                        onBack={() => goTo('publication')}
                        consent={consent}
                        onConsentChange={setConsent}
                        requireConsent={!isEditMode}
                        isEditMode={isEditMode}
                        onSubmit={handleExperimentDataSubmit}
                        initialData={experimentData}
                        readOnly={false}
                        imageOnly={imageOnly}
                        setImageOnly={setImageOnly}
                        onImportMetadata={handleImportMetadata}
                        initialGroups={draftGroups}
                        onGroupsChange={setCurrentGroups}
                    />
                )}
            </div>
            </div>
        </div>
    );
}
